import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type { PlatformTaskReminder } from '../models/assigned-task.models';
import type {
  DashboardFunnelFilters,
  FunnelAnalytics,
} from '../models/funnel-analytics.models';

export type PlatformDashboardScope = 'hq' | 'all';

/**
 * Platform dashboard metrics (v1). Scope: HQ for platform staff, all franchises for super admin.
 * See API `platform-dashboard.types.ts` for full metric definitions.
 */
export type PlatformDashboardStats = {
  scope: PlatformDashboardScope;
  leads: {
    total: number;
    newToday: number;
    qualified: number;
    enrolled: number;
    conversionRate: number;
  };
  applicants: {
    total: number;
    newToday: number;
    documentsPending: number;
    offersReceived: number;
    completedPlacements: number;
  };
  tasks: {
    leadsAssignedToMe: number;
    applicantsAssignedToMe: number;
  };
};

@Injectable({ providedIn: 'root' })
export class PlatformDashboardService {
  constructor(private readonly api: ApiService) {}

  getStats() {
    return this.api
      .get<ApiEnvelope<PlatformDashboardStats>>('platform/dashboard/stats')
      .pipe(map((response) => response.data));
  }

  getTaskReminder() {
    return this.api
      .get<ApiEnvelope<PlatformTaskReminder>>('platform/dashboard/task-reminder')
      .pipe(map((response) => response.data));
  }

  getLeadFunnel(filters: DashboardFunnelFilters) {
    return this.api
      .get<ApiEnvelope<FunnelAnalytics>>(`platform/dashboard/funnel/leads${this.toFunnelQuery(filters)}`)
      .pipe(map((response) => response.data));
  }

  getApplicantFunnel(filters: DashboardFunnelFilters) {
    return this.api
      .get<ApiEnvelope<FunnelAnalytics>>(`platform/dashboard/funnel/applicants${this.toFunnelQuery(filters)}`)
      .pipe(map((response) => response.data));
  }

  private toFunnelQuery(filters: DashboardFunnelFilters): string {
    const params = new URLSearchParams();
    params.set('dateRange', filters.dateRange);

    if (filters.createdFrom) {
      params.set('createdFrom', filters.createdFrom);
    }
    if (filters.createdTo) {
      params.set('createdTo', filters.createdTo);
    }
    if (filters.ownerUserId) {
      params.set('ownerUserId', filters.ownerUserId);
    }

    const query = params.toString();
    return query ? `?${query}` : '';
  }
}
